import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { GoogleMap, useJsApiLoader, InfoWindow } from '@react-google-maps/api'
import { MarkerClusterer } from '@googlemaps/markerclusterer'
import {
  Box,
  Typography,
  TextField,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Paper,
  Skeleton,
  Menu,
  MenuList,
  CircularProgress
} from '@mui/material'
import {
  LocationOn as LocationIcon,
  Close as CloseIcon,
  Fullscreen as FullscreenIcon,
  FullscreenExit as FullscreenExitIcon,
  Add as AddIcon,
  Remove as RemoveIcon,
  Search as SearchIcon,
  Apps as AppsIcon,
  Sort as SortIcon
} from '@mui/icons-material'
import PropertyCard, { type PropertyData } from '../components/PropertyCard'
import Pagination from '../components/Pagination'
import { useContentByEmbed } from '../hooks/useContentByEmbed'
import { config } from '../config/env'

// Tipos para las propiedades
export interface Property {
  id: string
  title: string
  price: number
  address: string
  city: string
  state: string
  zipCode: string
  bedrooms: number
  bathrooms: number
  squareFeet: number
  latitude: number
  longitude: number
  imageUrl?: string
  description?: string
  propertyType?: string
  status?: 'for-sale' | 'for-rent' | 'sold'
  url?: string
}

// Tipos para los filtros
export interface PropertyFilters {
  minPrice: number
  maxPrice: number
  minBedrooms: number
  maxBedrooms: number
  minBathrooms: number
  maxBathrooms: number
  minSquareFeet: number
  maxSquareFeet: number
  minYearBuilt: number
  maxYearBuilt: number
  propertyType: string
  subtype: string
  status: string
}

// Props del widget
export interface PropertyMapWidgetProps {
  embedId: string
  title?: string
  subtitle?: string
  apiKey?: string // Opcional: puede venir de contentData
  center?: { lat: number; lng: number }
  zoom?: number
  onPropertyClick?: (property: Property) => void
  onFiltersChange?: (filters: PropertyFilters) => void
  height?: string | number
  showFilters?: boolean
  showSearch?: boolean
  clusterMarkers?: boolean
}

const defaultCenter = {
  lat: 28.5383, // Orlando, FL
  lng: -81.3792
}

const defaultZoom = 12


// Campos que consume la vista de lista/mapa. Se mandan como `fields` para que el backend
// proyecte la respuesta; es opt-in, cualquier consumidor que no lo mande recibe todo.
const LIST_FIELDS = [
  'id', 'address', 'listPrice', 'city', 'state', 'postalCode',
  'bedrooms', 'bathrooms', 'area', 'geo', 'photos[0]', 'propertyType', 'url',
].join(',')

const PropertyMapWidget: React.FC<PropertyMapWidgetProps> = ({
  embedId,
  apiKey,
  center = defaultCenter,
  zoom = defaultZoom,
  onPropertyClick,
  onFiltersChange,
  height = '800px',
  showFilters = true,
  clusterMarkers = true
}) => {
  const [map, setMap] = useState<google.maps.Map | null>(null)
  const [selectedProperty, setSelectedProperty] = useState<Property | null>(null)
  const [selectedPropertyId, setSelectedPropertyId] = useState<string | null>(null)
  const [markers, setMarkers] = useState<google.maps.Marker[]>([])
  const [filters, setFilters] = useState<PropertyFilters>({
    minPrice: 0,
    maxPrice: 0, // 0 = sin seleccionar, no se envía a la API
    minBedrooms: 0,
    maxBedrooms: 5,
    minBathrooms: 0,
    maxBathrooms: 5,
    minSquareFeet: 0,
    maxSquareFeet: 10000,
    minYearBuilt: 0,
    maxYearBuilt: new Date().getFullYear(),
    propertyType: 'all',
    subtype: 'all',
    status: 'all'
  })
  const [searchLocation, setSearchLocation] = useState('')
  const [debouncedSearchLocation, setDebouncedSearchLocation] = useState('')
  // Área de búsqueda por defecto (contentType.json_config.defaultSearch):
  // - defaultSearchAppliedRef: se decide UNA sola vez por montaje, cuando llega la primera
  //   respuesta que trae el config (el config viaja en la misma respuesta que los listings).
  // - userTouchedSearchRef: si el usuario ya tocó la caja (tipeó o la limpió) antes de que
  //   llegue la respuesta, el default NO se aplica y no le pisamos su búsqueda.
  const defaultSearchAppliedRef = useRef(false)
  const userTouchedSearchRef = useRef(false)
  const [searchCenter, setSearchCenter] = useState<{ lat: number; lng: number } | null>(null)
  const [searchRadius, setSearchRadius] = useState(50) // Radio en millas
  const [userLocation, setUserLocation] = useState<{ lat: number; lng: number } | null>(null)
  const [hasRequestedLocation, setHasRequestedLocation] = useState(false)

  // Leer URL params al cargar y autorellenar campos
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search)
    const city = urlParams.get('city')
    const minPrice = urlParams.get('minPrice')
    const maxPrice = urlParams.get('maxPrice')
    const beds = urlParams.get('beds')
    const baths = urlParams.get('baths')

    if (city) {
      setSearchLocation(city)
    }

    const newFilters: Partial<PropertyFilters> = {}
    if (minPrice) newFilters.minPrice = parseInt(minPrice) || 0
    if (maxPrice) newFilters.maxPrice = parseInt(maxPrice) || 0
    if (beds) newFilters.minBedrooms = parseInt(beds) || 0
    if (baths) newFilters.minBathrooms = parseInt(baths) || 0

    if (Object.keys(newFilters).length > 0) {
      setFilters(prev => ({ ...prev, ...newFilters }))
    }
  }, [])

  // Debounce del texto de búsqueda para no disparar un fetch por cada tecla
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchLocation(searchLocation)
    }, 500)
    return () => clearTimeout(timer)
  }, [searchLocation])

  // Obtener ubicación del usuario solo una vez al cargar el componente
  useEffect(() => {
    // Solo ejecutar una vez
    if (hasRequestedLocation || !navigator.geolocation) {
      return
    }

    setHasRequestedLocation(true)

    // Obtener ubicación solo una vez (no escuchar cambios continuos)
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords
        setUserLocation({ lat: latitude, lng: longitude })
        console.log('📍 Ubicación del usuario obtenida (una sola vez):', { latitude, longitude })
      },
      (error) => {
        console.log('Error obteniendo ubicación del usuario:', error)
        // Si falla la geolocalización, no hacer nada más
      },
      {
        enableHighAccuracy: true,
        timeout: 5000,
        maximumAge: 0
      }
    )
  }, [hasRequestedLocation])

  const [filtersOpen, setFiltersOpen] = useState(false)
  const [tempFilters, setTempFilters] = useState<PropertyFilters | null>(null)
  const [filteredProperties, setFilteredProperties] = useState<Property[]>([])
  const [isFullscreen, setIsFullscreen] = useState(false)
  const [currentZoom, setCurrentZoom] = useState(zoom)
  const [currentPage, setCurrentPage] = useState(1)
  const itemsPerPage = 6
  const [sortAnchorEl, setSortAnchorEl] = useState<null | HTMLElement>(null)
  const [sortBy, setSortBy] = useState<string>('latest')
  
  // Estado para el Select de prueba de Material-UI
  const [testAge, setTestAge] = useState<number>(10)

  // Estilos compartidos para los Select
  const selectSx = {
    borderRadius: { xs: '12px', md: '10px' },
    height: { xs: '52px', md: '48px' },
    backgroundColor: 'white',
    fontSize: { xs: '16px', md: '15px' },
    fontFamily: "'Montserrat', sans-serif",
    color: '#0e2340',
    padding: { xs: '14px 16px', md: '12px 16px' },
    paddingRight: '40px',
    transition: 'border-color 0.2s ease',
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: '#ddd',
      borderWidth: '1px'
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: '#ddd'
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: '#0e2340',
      borderWidth: '1px'
    },
    '& .MuiSelect-select': {
      color: '#0e2340',
      padding: 0
    },
    '& .MuiInputLabel-root': {
      color: '#C5C5C5'
    },
    '&.Mui-focused .MuiInputLabel-root': {
      color: '#0e2340'
    }
  }

  // Estilos compartidos para los TextField del diálogo More Options
  const dialogInputSx = {
    '& .MuiOutlinedInput-root': {
      borderRadius: { xs: '12px', md: '10px' },
      height: { xs: '52px', md: '48px' },
      backgroundColor: '#ffffff',
      fontSize: { xs: '16px', md: '15px' },
      fontFamily: "'Montserrat', sans-serif",
      color: '#0e2340',
      padding: { xs: '14px 16px', md: '12px 16px' },
      transition: 'border-color 0.2s ease',
      '& fieldset': {
        borderColor: '#ddd',
        borderWidth: '1px'
      },
      '&:hover fieldset': {
        borderColor: '#ddd'
      },
      '&.Mui-focused fieldset': {
        borderColor: '#0e2340',
        borderWidth: '1px'
      },
      '& input': {
        color: '#0e2340',
        '&::placeholder': {
          color: '#C5C5C5',
          opacity: 1
        }
      }
    }
  }


  // Convertir filtros a formato de API
  const apiFilters = useMemo(() => {
    const apiFilters: any = {}
    
    // Solo agregar filtros que no sean valores por defecto
    if (filters.minPrice > 0) apiFilters.minPrice = filters.minPrice
    // Solo enviar maxPrice si el usuario lo ha seleccionado explícitamente (0 = sin seleccionar)
    if (filters.maxPrice > 0) apiFilters.maxPrice = filters.maxPrice
    
    // Beds - usar minBedrooms como beds
    if (filters.minBedrooms > 0) apiFilters.beds = filters.minBedrooms
    
    // Baths - usar minBathrooms como baths
    if (filters.minBathrooms > 0) apiFilters.baths = filters.minBathrooms
    
    // Square feet - minarea y maxarea
    if (filters.minSquareFeet > 0) apiFilters.minarea = filters.minSquareFeet
    if (filters.maxSquareFeet < 10000) apiFilters.maxarea = filters.maxSquareFeet
    
    // Year built - minyearbuild y maxyearbuild
    if (filters.minYearBuilt > 0) apiFilters.minyearbuild = filters.minYearBuilt
    if (filters.maxYearBuilt < new Date().getFullYear()) apiFilters.maxyearbuild = filters.maxYearBuilt
    
    // Property type
    if (filters.propertyType !== 'all') apiFilters.type = filters.propertyType
    
    // Subtype
    if (filters.subtype !== 'all') apiFilters.subtype = filters.subtype
    
    // Agregar filtros de ubicación si existen
    // El searchLocation puede contener city, county, o zipcode
    if (debouncedSearchLocation.trim()) {
      // Intentar determinar si es city, county, o zipcode
      const trimmedLocation = debouncedSearchLocation.trim()

      // Si es solo números, probablemente es un zipcode
      if (/^\d+$/.test(trimmedLocation)) {
        apiFilters.zipcode = trimmedLocation
      } else {
        // Por defecto, asumir que es city
        apiFilters.city = trimmedLocation
      }
    }

    // Las coordenadas NO se envían como filtro: el backend las convierte en una ciudad
    // exacta (sin radio), así que vaciar el buscador terminaba achicando la búsqueda en vez
    // de ampliarla. searchCenter/userLocation solo se usan para centrar el mapa.

    // Ordenamiento server-side (solo price está soportado por el backend)
    if (sortBy === 'price-high') {
      apiFilters.sort = 'price'
      apiFilters.order = 'desc'
    } else if (sortBy === 'price-low') {
      apiFilters.sort = 'price'
      apiFilters.order = 'asc'
    }

    // Traer el máximo permitido por el gateway MLS para ordenar/paginar sobre más resultados
    apiFilters.pageSize = 50

    // Pedir solo los campos que este widget consume (ver transformContentDataToProperties).
    // Sin esto la respuesta trae 75 campos por propiedad -incluidos arrays de hasta 92 URLs
    // de fotos y los remarks completos- de los que se usan 13: 544KB contra 49KB.
    apiFilters.fields = LIST_FIELDS

    // Siempre retornar un objeto, aunque esté vacío
    return apiFilters
  }, [filters, debouncedSearchLocation, sortBy])

  const { 
    data: contentData, 
    isLoading: isLoadingContent, 
    error: contentError,
    isFetching
  } = useContentByEmbed(embedId, undefined, apiFilters)

  // Aplicar el "área de búsqueda por defecto" del content type (json_config.defaultSearch).
  // Se aplica UNA sola vez, cuando llega la primera respuesta con config, y solo si:
  // - la caja sigue vacía (si vino ?city= por URL ya está seteada y no se pisa), y
  // - el usuario no tocó la caja mientras cargaba.
  // El valor se pone VISIBLE en el input: el usuario ve por qué ve esas casas y,
  // si lo borra, busca en todo el estado (borrar = ampliar, sin re-aplicar nada).
  useEffect(() => {
    if (defaultSearchAppliedRef.current) return

    const rawConfig = (contentData as any)?.data?.contentType?.json_config
    if (rawConfig == null) return

    // json_config puede venir como objeto o como string JSON según el backend
    let jsonConfig: any = rawConfig
    if (typeof rawConfig === 'string') {
      try {
        jsonConfig = JSON.parse(rawConfig)
      } catch {
        jsonConfig = null
      }
    }

    // Ya vimos el config: decidimos una sola vez, se aplique o no
    defaultSearchAppliedRef.current = true

    const defaultSearch =
      typeof jsonConfig?.defaultSearch === 'string' ? jsonConfig.defaultSearch.trim() : ''
    if (!defaultSearch) return
    if (userTouchedSearchRef.current) return
    if (searchLocation !== '') return

    // Mismo valor en ambos estados para no esperar los 500ms de debounce
    setSearchLocation(defaultSearch)
    setDebouncedSearchLocation(defaultSearch)
  }, [contentData, searchLocation])

  useEffect(() => {
    console.log('=== PropertyMapWidget Debug ===')
    console.log('contentData', contentData)
    console.log('isLoadingContent', isLoadingContent)
    console.log('isFetching', isFetching)
    console.log('apiFilters', apiFilters)
  }, [contentData, isLoadingContent, isFetching, apiFilters])


  // Transform contentData to Property format
  const transformContentDataToProperties = useCallback((data: any): Property[] => {
    // Intentar ambas estructuras: data.data.data y data.data
    const dataArray = data?.data?.data || data?.data
    
    if (!dataArray || !Array.isArray(dataArray)) return []
    
    return dataArray
      .filter((item: any) => item) // Filtrar items null/undefined
      .map((item: any) => {
        // Las propiedades pueden venir directamente en item o en item.data
        const itemData = item.data || item
        
        // Debug: Log para ver qué datos de imagen están llegando
        // console.log('Property image data:', {
        //   id: itemData?.id || item?.id,
        //   coverImage: itemData?.coverImage,
        //   imageUrl: itemData?.imageUrl,
        //   image: itemData?.image,
        //   photo: itemData?.photo,
        //   photos: itemData?.photos,
        //   images: itemData?.images,
        //   thumbnail: itemData?.thumbnail
        // })
        
        return {
          id: item.id?.toString() || itemData?.id?.toString() || '',
          title: itemData?.address || 'Property',
          price: itemData?.listPrice || itemData?.price || 0,
          address: itemData?.address || '',
          city: itemData?.city || '',
          state: itemData?.state || '',
          zipCode: itemData?.postalCode || itemData?.zipCode || '',
          bedrooms: itemData?.bedrooms || itemData?.beds || 0,
          bathrooms: itemData?.bathrooms || itemData?.baths || 0,
          squareFeet: itemData?.area || itemData?.sqft || itemData?.squareFeet || itemData?.square_feet || 0,
          latitude: itemData?.geo?.lat || itemData?.latitude || 0,
          longitude: itemData?.geo?.lng || itemData?.longitude || 0,
          imageUrl: itemData?.photos?.[0] || itemData?.photo || itemData?.coverImage || itemData?.imageUrl || itemData?.image || itemData?.images?.[0] || itemData?.thumbnail || '',
          description: `${itemData?.bedrooms || itemData?.beds || 0} bed, ${itemData?.bathrooms || itemData?.baths || 0} bath, ${itemData?.area || itemData?.sqft || itemData?.squareFeet || itemData?.square_feet || 0} sqft`,
          propertyType: itemData?.propertyType?.toLowerCase() || 'house',
          status: 'for-sale' as const,
          url: itemData.url
        }
      })
  }, [])

  // Get properties from contentData
  const actualProperties = useMemo(() => {
    if (contentData?.data?.data || contentData?.data) {
      return transformContentDataToProperties(contentData)
    }
    return []
  }, [contentData, transformContentDataToProperties])

  // Total real de resultados reportado por el backend ({ success, page, pageSize, total, data })
  const totalListings = useMemo(() => {
    const backendTotal = Number((contentData as any)?.data?.total) || 0
    return Math.max(backendTotal, actualProperties.length)
  }, [contentData, actualProperties])

  // Incluye la ventana del debounce: apenas tipea ya cuenta como búsqueda en curso
  const isSearching =
    isLoadingContent || isFetching || searchLocation.trim() !== debouncedSearchLocation.trim()

  // Calculate map center from actual properties
  const mapCenter = useMemo(() => {
    if (actualProperties.length === 0) return center
    
    const validProperties = actualProperties.filter(p => p.latitude !== 0 && p.longitude !== 0)
    if (validProperties.length === 0) return center
    
    const avgLat = validProperties.reduce((sum, p) => sum + p.latitude, 0) / validProperties.length
    const avgLng = validProperties.reduce((sum, p) => sum + p.longitude, 0) / validProperties.length
    
    return { lat: avgLat, lng: avgLng }
  }, [actualProperties, center])

  // Obtener apiKey de contentData o de props
  // El apiKey puede venir en contentData.googleMapsApiKey, contentData.config?.apiKey, etc.
  // Revisa la estructura de contentData en la consola para ver dónde está
  const googleMapsApiKey = 
    (contentData as any)?.googleMapsApiKey || 
    (contentData as any)?.config?.googleMapsApiKey || 
    (contentData as any)?.apiKey || 
    apiKey || 
    config.GOOGLE_MAPS_API_KEY

  // Cargar Google Maps API
  const { isLoaded, loadError } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: googleMapsApiKey,
    libraries: ['places']
  })


  // Ordenar propiedades
  const sortedProperties = useMemo(() => {
    const sorted = [...actualProperties]
    switch (sortBy) {
      case 'price-high':
      case 'price-low':
        // El backend ya ordena por precio (sort/order en apiFilters)
        return sorted
      case 'beds':
        return sorted.sort((a, b) => b.bedrooms - a.bedrooms)
      case 'baths':
        return sorted.sort((a, b) => b.bathrooms - a.bathrooms)
      case 'year-built':
        // Si no hay yearBuilt, mantener el orden original
        return sorted.sort((a, b) => {
          const aYear = (a as any).yearBuilt || 0
          const bYear = (b as any).yearBuilt || 0
          return bYear - aYear
        })
      case 'latest':
      default:
        return sorted // Mantener orden original (más recientes primero)
    }
  }, [actualProperties, sortBy])

  // Actualizar propiedades filtradas cuando cambien los datos de la API
  useEffect(() => {
    // Los filtros ahora se manejan en el servidor, solo actualizamos las propiedades
    setFilteredProperties(sortedProperties)
    onFiltersChange?.(filters)
    // Resetear a la primera página cuando cambien los filtros o las propiedades
    setCurrentPage(1)
  }, [sortedProperties, filters, onFiltersChange])

  // Centrar el mapa cuando se carguen las propiedades
  useEffect(() => {
    console.log('actualProperties',actualProperties);
    if (isLoaded && map && actualProperties.length > 0) {
      const validProperties = actualProperties.filter(p => p.latitude !== 0 && p.longitude !== 0)
      
      if (validProperties.length > 0) {
        // Calcular el centro de todas las propiedades
        const avgLat = validProperties.reduce((sum, p) => sum + p.latitude, 0) / validProperties.length
        const avgLng = validProperties.reduce((sum, p) => sum + p.longitude, 0) / validProperties.length
        
        // Si hay múltiples propiedades, ajustar el zoom para que todas sean visibles
        if (validProperties.length > 1) {
          const bounds = new google.maps.LatLngBounds()
          validProperties.forEach(property => {
            bounds.extend(new google.maps.LatLng(property.latitude, property.longitude))
          })
          map.fitBounds(bounds)
        } else {
          // Si solo hay una propiedad, centrar y usar zoom por defecto
          map.setCenter({ lat: avgLat, lng: avgLng })
          map.setZoom(zoom)
        }
      }
    }
  }, [isLoaded, map, actualProperties, zoom])

  // Crear marcadores cuando el mapa esté listo y las propiedades cambien
  useEffect(() => {
    if (isLoaded && map) {
      // Limpiar marcadores existentes
      markers.forEach(marker => marker.setMap(null))
      
      // Filtrar propiedades con coordenadas válidas
      const validProperties = filteredProperties.filter(p => p.latitude !== 0 && p.longitude !== 0)
      
      if (validProperties.length === 0) {
        setMarkers([])
        return
      }

      // Crear nuevos marcadores
      const newMarkers = validProperties.map(property => {
        const marker = new google.maps.Marker({
          position: { lat: property.latitude, lng: property.longitude },
          map: map,
          title: property.title,
          icon: {
            url: 'https://maps.google.com/mapfiles/ms/icons/red-dot.png',
            scaledSize: new google.maps.Size(30, 30)
          }
        })

        // Agregar evento de click
        marker.addListener('click', () => {
          handleMarkerClick(property)
        })

        return marker
      })

      setMarkers(newMarkers)

      // Configurar clustering si está habilitado
      if (clusterMarkers && newMarkers.length > 0) {
        new MarkerClusterer({
          map,
          markers: newMarkers
        })
      }
    }
  }, [isLoaded, map, filteredProperties, clusterMarkers])

  // Manejar cambios en filtros
  const handleFilterChange = (filterName: keyof PropertyFilters, value: any) => {
    setFilters(prev => ({
      ...prev,
      [filterName]: value
    }))
  }

  // Manejar cambios en filtros temporales (para el diálogo)
  const handleTempFilterChange = (filterName: keyof PropertyFilters, value: any) => {
    if (tempFilters) {
      setTempFilters(prev => ({
        ...prev!,
        [filterName]: value
      }))
    }
  }

  // Abrir diálogo y inicializar filtros temporales
  const handleOpenFiltersDialog = () => {
    setTempFilters({ ...filters })
    setFiltersOpen(true)
  }

  // Guardar cambios y cerrar diálogo
  const handleSaveFilters = () => {
    if (tempFilters) {
      setFilters(tempFilters)
    }
    setFiltersOpen(false)
    setTempFilters(null)
  }

  // Cerrar diálogo sin guardar
  const handleCloseFiltersDialog = () => {
    setFiltersOpen(false)
    setTempFilters(null)
  }

  // Convertir Property a PropertyData para el PropertyCard existente
  const convertToPropertyData = (property: Property): PropertyData => {
    // Si no hay imagen, usar una imagen única basada en el ID para evitar que todas tengan la misma
    const fallbackImage = property.id 
      ? `https://images.unsplash.com/photo-1564013799919-ab600027ffc6?w=400&id=${property.id}`
      : 'https://images.unsplash.com/photo-1564013799919-ab600027ffc6?w=400'
    
    return {
      id: property.id,
      image: property.imageUrl || fallbackImage,
      price: `$${property.price.toLocaleString()}`,
      address: property.title,
      city: property.city,
      state: property.state,
      zipCode: property.zipCode,
      beds: property.bedrooms,
      baths: property.bathrooms,
      squareFeet: property.squareFeet,
      isFeatured: property.status === 'for-sale',
      isFavorite: false,
      url: property.url,
    }
  }

  // Manejar selección de propiedad desde tarjeta
  const handlePropertyCardClick = (propertyData: PropertyData) => {
    // Encontrar la propiedad original por ID
    const originalProperty = actualProperties.find(p => p.id === propertyData.id)
    if (!originalProperty) return
    
    setSelectedPropertyId(originalProperty.id)
    setSelectedProperty(originalProperty)
    
    // Centrar el mapa en la propiedad seleccionada
    if (map) {
      map.setCenter({ lat: originalProperty.latitude, lng: originalProperty.longitude })
      map.setZoom(16)
    }
    
    onPropertyClick?.(originalProperty)
  }

  // Manejar click en marcador del mapa
  const handleMarkerClick = (property: Property) => {
    setSelectedPropertyId(property.id)
    setSelectedProperty(property)
    onPropertyClick?.(property)
  }

  // Manejar ver detalles
  const handleViewDetails = (propertyData: PropertyData) => {
    // Encontrar la propiedad original por ID
    const originalProperty = actualProperties.find(p => p.id === propertyData.id)
    if (!originalProperty) return
    
    console.log('Ver detalles de:', originalProperty.title)

    // Si tiene URL, redirigir a esa página en una nueva pestaña
    if (originalProperty.url) {
      window.open(originalProperty.url, '_blank', 'noopener,noreferrer')
      return
    }

    // Si no tiene URL, mantener el comportamiento anterior
    // Aquí puedes implementar navegación a página de detalles
  }

  // Buscar ubicación y filtrar propiedades
  const handleSearchLocation = () => {
    if (!searchLocation.trim()) {
      // Si se limpia la búsqueda, quitar el filtro de ubicación
      setSearchCenter(null)
      return
    }

    const geocoder = new google.maps.Geocoder()
    geocoder.geocode({ address: searchLocation }, (results, status) => {
      if (status === 'OK' && results && results[0]) {
        const location = results[0].geometry.location
        const searchCenterCoords = {
          lat: location.lat(),
          lng: location.lng()
        }
        
        // Establecer el centro de búsqueda para filtrar propiedades
        setSearchCenter(searchCenterCoords)
        
        // Centrar el mapa en la ubicación buscada
        map?.setCenter(searchCenterCoords)
        map?.setZoom(15)
        
        console.log(`Buscando propiedades dentro de ${searchRadius} millas de ${searchLocation}`)
      } else {
        console.error('Error al geocodificar la ubicación:', status)
      }
    })
  }

  // Limpiar búsqueda de ubicación
  const clearLocationSearch = () => {
    userTouchedSearchRef.current = true // limpiar también cuenta como interacción del usuario
    setSearchLocation('')
    setSearchCenter(null)
  }

  // Manejar menú de ordenamiento
  const handleSortClick = (event: React.MouseEvent<HTMLElement>) => {
    setSortAnchorEl(event.currentTarget)
  }

  const handleSortClose = () => {
    setSortAnchorEl(null)
  }

  const handleSortSelect = (sortOption: string) => {
    setSortBy(sortOption)
    setCurrentPage(1) // Resetear a la primera página al cambiar ordenamiento
    handleSortClose()
  }

  // Formatear precio
  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('es-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0
    }).format(price)
  }

  // Manejar zoom in
  const handleZoomIn = () => {
    if (map) {
      const newZoom = map.getZoom() || currentZoom
      map.setZoom(newZoom + 1)
      setCurrentZoom(newZoom + 1)
    }
  }

  // Manejar zoom out
  const handleZoomOut = () => {
    if (map) {
      const newZoom = map.getZoom() || currentZoom
      map.setZoom(newZoom - 1)
      setCurrentZoom(newZoom - 1)
    }
  }

  // Manejar pantalla completa
  const handleFullscreen = () => {
    const mapContainer = document.getElementById('map-container')
    if (!mapContainer) return

    if (!document.fullscreenElement) {
      mapContainer.requestFullscreen().catch(err => {
        console.error('Error al entrar en pantalla completa:', err)
      })
      setIsFullscreen(true)
    } else {
      document.exitFullscreen()
      setIsFullscreen(false)
    }
  }

  // Escuchar cambios de zoom del mapa
  useEffect(() => {
    if (map) {
      const listener = map.addListener('zoom_changed', () => {
        const newZoom = map.getZoom() || currentZoom
        setCurrentZoom(newZoom)
      })
      return () => {
        google.maps.event.removeListener(listener)
      }
    }
  }, [map, currentZoom])

  // Escuchar cambios de pantalla completa
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement)
    }
    document.addEventListener('fullscreenchange', handleFullscreenChange)
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange)
    }
  }, [])

  // Renderizar filtros
  const renderFilters = () => {
    if (!showFilters) return null

    const propertyTypes = [
      'residential',
      'rental',
      'mobilehome',
      'condominium',
      'multifamily',
      'commercial',
      'commerciallease',
      'land',
      'farm'
    ]

    const propertyTypesLabels: Record<string, string> = {
      'residential': 'Residential',
      'rental': 'Rental',
      'mobilehome': 'Mobile Home',
      'condominium': 'Condominium',
      'multifamily': 'Multi Family',
      'commercial': 'Commercial',
      'commerciallease': 'Commercial Lease',
      'land': 'Land',
      'farm': 'Farm'
    }

    const propertySubtypes = [
      'apartment',
      'boatslip',
      'singlefamilyresidence',
      'deededparking',
      'cabin',
      'condominium',
      'duplex',
      'manufacturedhome',
      'ownyourown',
      'quadruplex',
      'stockcooperative',
      'townhouse',
      'timeshare',
      'triplex',
      'manufacturedonland',
      'farmandranch',
      'agriculture',
      'business',
      'hotelmotel',
      'industrial',
      'mixeduse',
      'officespace',
      'retail',
      'unimprovedland',
      'warehouse'
    ]

    const propertySubtypesLabels: Record<string, string> = {
      'apartment': 'Apartment',
      'boatslip': 'Boat Slip',
      'singlefamilyresidence': 'Single Family Residence',
      'deededparking': 'Deeded Parking',
      'cabin': 'Cabin',
      'condominium': 'Condominium',
      'duplex': 'Duplex',
      'manufacturedhome': 'Manufactured Home',
      'ownyourown': 'Own Your Own',
      'quadruplex': 'Quadruplex',
      'stockcooperative': 'Stock Cooperative',
      'townhouse': 'Townhouse',
      'timeshare': 'Timeshare',
      'triplex': 'Triplex',
      'manufacturedonland': 'Manufactured on Land',
      'farmandranch': 'Farm and Ranch',
      'agriculture': 'Agriculture',
      'business': 'Business',
      'hotelmotel': 'Hotel/Motel',
      'industrial': 'Industrial',
      'mixeduse': 'Mixed Use',
      'officespace': 'Office Space',
      'retail': 'Retail',
      'unimprovedland': 'Unimproved Land',
      'warehouse': 'Warehouse'
    }

    return (
      <Dialog 
        open={filtersOpen} 
        onClose={handleCloseFiltersDialog} 
        maxWidth="sm" 
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '12px',
            maxWidth: '900px',
            fontFamily: "'Montserrat', sans-serif"
          }
        }}
        BackdropProps={{
          sx: {
            backgroundColor: 'rgba(0, 0, 0, 0.6)'
          }
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '24px 32px',
            borderBottom: '2px solid #e5e5e5',
            fontFamily: "'Montserrat', sans-serif"
          }}
        >
          <Typography 
            variant="h6" 
            sx={{
              fontSize: '28px',
              fontWeight: 600,
              color: '#0e2340',
              fontFamily: "'Montserrat', sans-serif",
              margin: 0
            }}
          >
            More Options
          </Typography>
          <IconButton 
            onClick={handleCloseFiltersDialog} 
            sx={{
              background: 'none',
              border: 'none',
              fontSize: '36px',
              color: '#666666',
              cursor: 'pointer',
              lineHeight: 1,
              fontFamily: "'Montserrat', sans-serif",
              padding: 0,
              '&:hover': {
                backgroundColor: 'transparent',
                opacity: 0.7
              }
            }}
          >
            ×
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ padding: '32px', fontFamily: "'Montserrat', sans-serif" }}>
          <Box>
            {/* Square Feet */}
            <Box sx={{ mb: 4 }}>
              <Typography 
                variant="body2" 
                sx={{
                  display: 'block',
                  fontSize: '20px',
                  fontWeight: 600,
                  color: '#0e2340',
                  marginBottom: '12px',
                  textAlign: 'left',
                  fontFamily: "'Montserrat', sans-serif"
                }}
              >
                Square Feet
              </Typography>
              <Box display="flex" alignItems="center" gap={2}>
                <TextField
                  placeholder="Min Sqft"
                  value={tempFilters?.minSquareFeet ?? ''}
                  onChange={(e) => handleTempFilterChange('minSquareFeet', parseInt(e.target.value) || 0)}
                  size="small"
                  type="number"
                  fullWidth
                  sx={{
                    flex: 1,
                    ...dialogInputSx
                  }}
                />
                <Typography 
                  variant="body2" 
                  sx={{
                    color: '#666666',
                    fontSize: '14px',
                    fontFamily: "'Montserrat', sans-serif"
                  }}
                >
                  to
                </Typography>
                <TextField
                  placeholder="Max Sqft"
                  value={tempFilters?.maxSquareFeet ?? ''}
                  onChange={(e) => handleTempFilterChange('maxSquareFeet', parseInt(e.target.value) || 10000)}
                  size="small"
                  type="number"
                  fullWidth
                  sx={{
                    flex: 1,
                    ...dialogInputSx
                  }}
                />
              </Box>
            </Box>

            {/* Year Built */}
            <Box sx={{ mb: 4 }}>
              <Typography 
                variant="body2" 
                sx={{
                  display: 'block',
                  fontSize: '20px',
                  fontWeight: 600,
                  color: '#0e2340',
                  marginBottom: '12px',
                  textAlign: 'left',
                  fontFamily: "'Montserrat', sans-serif"
                }}
              >
                Year Built
              </Typography>
              <Box display="flex" alignItems="center" gap={2}>
                <TextField
                  placeholder="Min Year"
                  value={tempFilters?.minYearBuilt ?? ''}
                  onChange={(e) => handleTempFilterChange('minYearBuilt', parseInt(e.target.value) || 0)}
                  size="small"
                  type="number"
                  fullWidth
                  sx={{
                    flex: 1,
                    ...dialogInputSx
                  }}
                />
                <Typography 
                  variant="body2" 
                  sx={{
                    color: '#666666',
                    fontSize: '14px',
                    fontFamily: "'Montserrat', sans-serif"
                  }}
                >
                  to
                </Typography>
                <TextField
                  placeholder="Max Year"
                  value={tempFilters?.maxYearBuilt ?? ''}
                  onChange={(e) => handleTempFilterChange('maxYearBuilt', parseInt(e.target.value) || new Date().getFullYear())}
                  size="small"
                  type="number"
                  fullWidth
                  sx={{
                    flex: 1,
                    ...dialogInputSx
                  }}
                />
              </Box>
            </Box>

            {/* Property Type */}
            <Box sx={{ mb: 4 }}>
              <Typography 
                variant="body2" 
                sx={{
                  display: 'block',
                  fontSize: '20px',
                  fontWeight: 600,
                  color: '#0e2340',
                  marginBottom: '12px',
                  textAlign: 'left',
                  fontFamily: "'Montserrat', sans-serif"
                }}
              >
                Type
              </Typography>
              <Box
                sx={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 1.5
                }}
              >
                {propertyTypes.map((type) => {
                  const isSelected = tempFilters?.propertyType === type
                  return (
                    <Button
                      key={type}
                      variant={isSelected ? 'contained' : 'outlined'}
                      onClick={() => handleTempFilterChange('propertyType', isSelected ? 'all' : type)}
                      sx={{
                        borderRadius: { xs: '12px', md: '10px' },
                        height: { xs: '52px', md: '48px' },
                        borderColor: isSelected ? '#0e2340' : '#dddddd',
                        borderWidth: '1px',
                        color: isSelected ? '#ffffff' : '#666666',
                        backgroundColor: isSelected ? '#0e2340' : '#ffffff',
                        textTransform: 'none',
                        fontWeight: 500,
                        padding: { xs: '14px 20px', md: '12px 20px' },
                        fontSize: { xs: '16px', md: '15px' },
                        fontFamily: "'Montserrat', sans-serif",
                        '&:hover': {
                          borderColor: isSelected ? '#0e2340' : '#dddddd',
                          backgroundColor: isSelected ? '#0e2340' : '#ffffff',
                          opacity: isSelected ? 0.9 : 1
                        }
                      }}
                    >
                      {propertyTypesLabels[type] || type}
                    </Button>
                  )
                })}
              </Box>
            </Box>

            {/* Subtype */}
            <Box>
              <Typography 
                variant="body2" 
                sx={{
                  display: 'block',
                  fontSize: '20px',
                  fontWeight: 600,
                  color: '#0e2340',
                  marginBottom: '12px',
                  textAlign: 'left',
                  fontFamily: "'Montserrat', sans-serif"
                }}
              >
                Subtype
              </Typography>
              <Box
                sx={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 1.5
                }}
              >
                {propertySubtypes.map((subtype) => {
                  const isSelected = tempFilters?.subtype === subtype
                  return (
                    <Button
                      key={subtype}
                      variant={isSelected ? 'contained' : 'outlined'}
                      onClick={() => handleTempFilterChange('subtype', isSelected ? 'all' : subtype)}
                      sx={{
                        borderRadius: { xs: '12px', md: '10px' },
                        height: { xs: '52px', md: '48px' },
                        borderColor: isSelected ? '#0e2340' : '#dddddd',
                        borderWidth: '1px',
                        color: isSelected ? '#ffffff' : '#666666',
                        backgroundColor: isSelected ? '#0e2340' : '#ffffff',
                        textTransform: 'none',
                        fontWeight: 500,
                        padding: { xs: '14px 20px', md: '12px 20px' },
                        fontSize: { xs: '16px', md: '15px' },
                        fontFamily: "'Montserrat', sans-serif",
                        '&:hover': {
                          borderColor: isSelected ? '#0e2340' : '#dddddd',
                          backgroundColor: isSelected ? '#0e2340' : '#ffffff',
                          opacity: isSelected ? 0.9 : 1
                        }
                      }}
                    >
                      {propertySubtypesLabels[subtype] || subtype}
                    </Button>
                  )
                })}
              </Box>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions 
          sx={{ 
            padding: '24px 32px',
            borderTop: '2px solid #e5e5e5',
            margin: 0
          }}
        >
          <Button
            variant="contained"
            onClick={handleSaveFilters}
            fullWidth
            sx={{
              backgroundColor: '#0e2340',
              color: '#ffffff',
              borderRadius: { xs: '12px', md: '10px' },
              height: { xs: '52px', md: '48px' },
              padding: { xs: '14px 24px', md: '12px 24px' },
              fontSize: { xs: '16px', md: '15px' },
              fontWeight: 700,
              fontFamily: "'Montserrat', sans-serif",
              textTransform: 'none',
              '&:hover': {
                backgroundColor: '#0e2340',
                opacity: 0.9
              }
            }}
          >
            Apply Filters
          </Button>
        </DialogActions>
      </Dialog>
    )
  }

  // Mostrar error si no se puede cargar Google Maps
  if (loadError) {
    return (
      <Alert severity="error">
        Error loading Google Maps. Please verify your API key.
      </Alert>
    )
  }

  // Mostrar error de contentData
  if (contentError) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        Error loading properties: {contentError.message}
      </Alert>
    )
  }

  // Cargar fuente Montserrat desde Google Fonts
  useEffect(() => {
    const link = document.createElement('link')
    link.href = 'https://fonts.googleapis.com/css2?family=Montserrat:wght@400;600;700;800&display=swap'
    link.rel = 'stylesheet'
    document.head.appendChild(link)
    return () => {
      document.head.removeChild(link)
    }
  }, [])

  return (
    <Box sx={{ 
      width: '100%', 
      height: 'auto',
      minHeight: height,
      fontFamily: "'Montserrat', sans-serif",
      display: 'flex',
      flexDirection: 'column',
      overflow: 'visible',
      position: 'relative'
    }}>
    
      {/* Barra de filtros - Responsive con Grid */}
      <Paper 
        elevation={0} 
        sx={{ 
          p: { xs: '16px', md: '6px 20px' },
          mb: { xs: 1.5, md: 1 },
          backgroundColor: 'transparent',
          //borderBottom: { xs: '1px solid #e0e0e0', md: 'none' },
          width: '100%',
          fontFamily: "'Montserrat', sans-serif",
          maxWidth: { md: '1440px' },
          margin: { md: '0 auto' }
        }}
      >
        {/* Filtros con Grid */}
        <Box 
          sx={{ 
            display: 'grid',
            gridTemplateColumns: { xs: '1fr 1fr', md: '2fr 1fr 1fr 1fr 1fr 2fr' },
            gap: 1.5,
            mb: 2,
            mt: { md: 3 }
          }}
        >
          {/* Campo de búsqueda */}
          <Box sx={{ gridColumn: { xs: '1 / -1', md: 'auto' }, position: 'relative' }}>
          <TextField
            placeholder="City or Postal Code"
            value={searchLocation}
            onChange={(e) => {
              userTouchedSearchRef.current = true
              setSearchLocation(e.target.value)
            }}
            size="small"
            fullWidth
            InputProps={{
                startAdornment: (
                  <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', ml: 0.5 }}>
                    <LocationIcon sx={{ color: 'text.secondary' }} />
                  </Box>
                ),
              endAdornment: searchLocation && (
                <IconButton
                  size="small"
                  onClick={clearLocationSearch}
                  sx={{ mr: 1 }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              )
            }}
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                handleSearchLocation()
              }
            }}
            sx={{ 
              '& .MuiOutlinedInput-root': {
                  borderRadius: { xs: '12px', md: '10px' },
                  height: { xs: '52px', md: '48px' },
                backgroundColor: 'white',
                  fontSize: { xs: '16px', md: '15px' },
                fontFamily: "'Montserrat', sans-serif",
                color: '#0e2340',
                  padding: { xs: '14px 16px 14px 48px', md: '12px 16px 12px 12px' },
                  paddingLeft: { md: '12px' },
                  '& .MuiInputAdornment-root': {
                    marginRight: { md: '8px' }
                  },
                transition: 'border-color 0.2s ease',
                '& fieldset': {
                  borderColor: '#ddd',
                  borderWidth: '1px'
                },
                '&:hover fieldset': {
                  borderColor: '#ddd'
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#0e2340',
                  borderWidth: '1px'
                },
                '& input': {
                  color: '#0e2340',
                  '&::placeholder': {
                    color: '#C5C5C5',
                    opacity: 1
                  }
                }
              }
            }}
          />
            <Box 
              sx={{ 
                display: { xs: 'flex', md: 'none' },
                position: 'absolute',
                left: '16px',
                top: '50%',
                transform: 'translateY(-50%)',
                zIndex: 2,
                pointerEvents: 'none',
                alignItems: 'center'
              }}
            >
              <LocationIcon sx={{ color: 'text.secondary', fontSize: '20px' }} />
        </Box>
          </Box>
          <Box>
            <FormControl size="small" fullWidth>
              <InputLabel id="min-price-label">Min Price</InputLabel>
            <Select
                labelId="min-price-label"
                id="min-price-select"
              value={filters.minPrice}
                label="Min Price"
              onChange={(e) => handleFilterChange('minPrice', e.target.value)}
                variant="outlined"
              renderValue={(selected) => {
                  const val = selected as number
                  if (val === 0) return 'Min Price'
                  if (val >= 1000000) {
                    const millions = val / 1000000
                    // Si es un número entero, mostrar sin decimales
                    if (millions % 1 === 0) return `$${millions}M`
                    return `$${millions.toFixed(1)}M`
                  }
                  return `$${val / 1000}K`
                }}
                sx={selectSx}
            >
              <MenuItem value={0}>Min Price</MenuItem>
              <MenuItem value={100000}>$100K</MenuItem>
              <MenuItem value={200000}>$200K</MenuItem>
              <MenuItem value={300000}>$300K</MenuItem>
              <MenuItem value={400000}>$400K</MenuItem>
              <MenuItem value={500000}>$500K</MenuItem>
              <MenuItem value={750000}>$750K</MenuItem>
              <MenuItem value={1000000}>$1M</MenuItem>
              <MenuItem value={2000000}>$2M</MenuItem>
            </Select>
          </FormControl>
          </Box>
          <Box>
            <FormControl size="small" fullWidth>
              <InputLabel id="max-price-label">Max Price</InputLabel>
            <Select
                labelId="max-price-label"
                id="max-price-select"
              value={filters.maxPrice}
                label="Max Price"
              onChange={(e) => handleFilterChange('maxPrice', e.target.value)}
                variant="outlined"
              renderValue={(selected) => {
                  const val = selected as number
                  if (val === 0) return 'Max Price'
                  if (val >= 1000000) {
                    const millions = val / 1000000
                    // Si es un número entero, mostrar sin decimales
                    if (millions % 1 === 0) return `$${millions}M`
                    return `$${millions.toFixed(1)}M`
                  }
                  return `$${val / 1000}K`
                }}
                sx={selectSx}
            >
              <MenuItem value={0}>Max Price</MenuItem>
              <MenuItem value={200000}>$200K</MenuItem>
              <MenuItem value={300000}>$300K</MenuItem>
              <MenuItem value={400000}>$400K</MenuItem>
              <MenuItem value={500000}>$500K</MenuItem>
              <MenuItem value={750000}>$750K</MenuItem>
              <MenuItem value={1000000}>$1M</MenuItem>
              <MenuItem value={1500000}>$1.5M</MenuItem>
              <MenuItem value={2000000}>$2M</MenuItem>
            </Select>
          </FormControl>
        </Box>
          <Box>
            <FormControl size="small" fullWidth>
              <InputLabel id="beds-label">Beds</InputLabel>
            <Select
                labelId="beds-label"
                id="beds-select"
              value={filters.minBedrooms}
                label="Beds"
              onChange={(e) => handleFilterChange('minBedrooms', e.target.value)}
                variant="outlined"
              renderValue={(selected) => {
                  const val = selected as number
                  if (val === 0) return 'Beds'
                  return `${val}+`
                }}
                sx={selectSx}
            >
              <MenuItem value={0}>Beds</MenuItem>
              <MenuItem value={1}>1+</MenuItem>
              <MenuItem value={2}>2+</MenuItem>
              <MenuItem value={3}>3+</MenuItem>
              <MenuItem value={4}>4+</MenuItem>
              <MenuItem value={5}>5+</MenuItem>
            </Select>
          </FormControl>
          </Box>
          <Box>
            <FormControl size="small" fullWidth>
              <InputLabel id="baths-label">Baths</InputLabel>
            <Select
                labelId="baths-label"
                id="baths-select"
              value={filters.minBathrooms}
                label="Baths"
              onChange={(e) => handleFilterChange('minBathrooms', e.target.value)}
                variant="outlined"
              renderValue={(selected) => {
                  const val = selected as number
                  if (val === 0) return 'Baths'
                  return `${val}+`
                }}
                sx={selectSx}
            >
              <MenuItem value={0}>Baths</MenuItem>
              <MenuItem value={1}>1+</MenuItem>
              <MenuItem value={2}>2+</MenuItem>
              <MenuItem value={3}>3+</MenuItem>
              <MenuItem value={4}>4+</MenuItem>
              <MenuItem value={5}>5+</MenuItem>
            </Select>
          </FormControl>
        </Box>
          {/* Botones en desktop - misma fila */}
          <Box sx={{ display: { xs: 'none', md: 'block' } }}>
            <Box display="flex" gap={1.5} sx={{ height: '48px' }}>
          <Button
            variant="contained"
            onClick={handleOpenFiltersDialog}
            startIcon={<AppsIcon />}
            sx={{
              flex: 1,
                  height: '100%',
              backgroundColor: '#b08b4f',
              color: 'white',
                  borderRadius: '10px',
                  padding: '12px 20px',
                  fontSize: '15px',
              fontFamily: "'Montserrat', sans-serif",
              fontWeight: 600,
              textTransform: 'none',
              transition: 'background-color 0.3s ease',
              '&:hover': {
                backgroundColor: '#b08b4f',
                opacity: 0.9
              }
            }}
          >
            More
          </Button>

          <Button
            variant="text"
            onClick={() => {
              setFilters({
                minPrice: 0,
                maxPrice: 0,
                minBedrooms: 0,
                maxBedrooms: 5,
                minBathrooms: 0,
                maxBathrooms: 5,
                minSquareFeet: 0,
                maxSquareFeet: 10000,
                minYearBuilt: 0,
                maxYearBuilt: new Date().getFullYear(),
                propertyType: 'all',
                subtype: 'all',
                status: 'all'
              })
              clearLocationSearch()
            }}
            sx={{
              height: '100%',
              backgroundColor: 'transparent',
              color: '#0e2340',
              border: 'none',
              borderRadius: '10px',
              padding: '12px 20px',
              fontSize: '15px',
              fontFamily: "'Montserrat', sans-serif",
              fontWeight: 600,
              textTransform: 'none',
              textDecoration: 'underline',
              textUnderlineOffset: '4px',
              transition: 'all 0.2s ease',
              '&:hover': {
                backgroundColor: 'transparent',
                textDecoration: 'underline',
                opacity: 0.8
              }
            }}
          >
            Reset Filters
          </Button>
        </Box>
          </Box>
          </Box>

        {/* Botones abajo - solo mobile */}
        <Box sx={{ display: { xs: 'flex', md: 'none' }, gap: 1.5, mt: 1.5 }}>
            <Button
              variant="contained"
              onClick={handleOpenFiltersDialog}
            startIcon={<AppsIcon />}
              sx={{
              flex: 1,
              height: '52px',
                backgroundColor: '#b08b4f',
                color: 'white',
              borderRadius: '12px',
              padding: '14px 20px',
              fontSize: '16px',
                fontFamily: "'Montserrat', sans-serif",
                fontWeight: 600,
                textTransform: 'none',
                transition: 'background-color 0.3s ease',
                '&:hover': {
                  backgroundColor: '#b08b4f',
                  opacity: 0.9
                }
              }}
            >
            More
            </Button>

          <Button
            variant="outlined"
              onClick={() => {
                setFilters({
                  minPrice: 0,
                  maxPrice: 0,
                  minBedrooms: 0,
                  maxBedrooms: 5,
                  minBathrooms: 0,
                  maxBathrooms: 5,
                  minSquareFeet: 0,
                  maxSquareFeet: 10000,
                  minYearBuilt: 0,
                  maxYearBuilt: new Date().getFullYear(),
                  propertyType: 'all',
                  subtype: 'all',
                  status: 'all'
                })
                clearLocationSearch()
              }}
              sx={{
              flex: 1,
              height: '52px',
              backgroundColor: 'white',
                color: '#0e2340',
              border: '2px solid #0e2340',
              borderRadius: '12px',
              padding: '14px 20px',
              fontSize: '16px',
                fontFamily: "'Montserrat', sans-serif",
                fontWeight: 600,
              textTransform: 'none',
              transition: 'all 0.2s ease',
                '&:hover': {
                borderColor: '#0e2340',
                backgroundColor: 'white'
                }
              }}
            >
            Reset
          </Button>
          </Box>

      </Paper>


      {/* Layout principal: Propiedades a la izquierda, Mapa a la derecha */}
      <Box mt={-1} sx={{ 
        display: 'flex', 
        gap: 2, 
        minHeight: 'calc(100% - 80px)',
        overflow: 'visible',
        flexDirection: { xs: 'column', md: 'row' },
        alignItems: { xs: 'stretch', md: 'flex-start' }
      }}>
        {/* Columna izquierda: Lista de propiedades */}
        <Box sx={{ 
          width: { xs: '100%', md: '40%' }, 
          display: { xs: 'block', md: 'block' }, 
          flexShrink: 0
        }}>
          <Paper elevation={0} sx={{ 
            display: 'flex', 
            flexDirection: 'column', 
            backgroundColor: 'transparent'
          }}>
            {/* Header con contador y botón Sort */}
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center', 
              p: 2, 
              pb: 1.5,
              //borderBottom: '1px solid #e0e0e0'
            }}>
              <Box>
                {isSearching ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CircularProgress size={16} sx={{ color: '#0e2340' }} />
                    <Typography
                      variant="h6"
                      sx={{
                        fontSize: '18px',
                        fontWeight: 600,
                        color: '#0e2340',
                        fontFamily: "'Montserrat', sans-serif"
                      }}
                    >
                      Searching...
                    </Typography>
                  </Box>
                ) : (
                  <>
                    <Typography
                      variant="h6"
                      sx={{
                        fontSize: '18px',
                        fontWeight: 600,
                        color: '#0e2340',
                        fontFamily: "'Montserrat', sans-serif"
                      }}
                    >
                      {totalListings} {totalListings === 1 ? 'Listing' : 'Listings'}
                    </Typography>
                    {totalListings > filteredProperties.length && filteredProperties.length > 0 && (
                      <Typography
                        variant="caption"
                        sx={{
                          display: 'block',
                          fontSize: '12px',
                          color: '#666666',
                          fontFamily: "'Montserrat', sans-serif"
                        }}
                      >
                        Showing first {filteredProperties.length} of {totalListings} listings
                      </Typography>
                    )}
                  </>
                )}
              </Box>
              <Button
                onClick={handleSortClick}
                startIcon={<SortIcon />}
                variant="outlined"
                sx={{
                  color: '#0e2340',
                  backgroundColor: '#ffffff',
                  borderColor: '#0e2340',
                  borderWidth: '1px',
                  borderRadius: '8px',
                  textTransform: 'none',
                  fontSize: '14px',
                  fontWeight: 500,
                  fontFamily: "'Montserrat', sans-serif",
                  padding: '8px 16px',
                  transition: 'all 0.2s ease',
                  '& .MuiSvgIcon-root': {
                    color: '#0e2340',
                    transition: 'color 0.2s ease'
                  },
                  '&:hover': {
                    backgroundColor: '#0e2340',
                    borderColor: '#0e2340',
                    color: '#ffffff',
                    '& .MuiSvgIcon-root': {
                      color: '#ffffff'
                    }
                  },
                  '&:active': {
                    backgroundColor: '#0e2340',
                    borderColor: '#0e2340',
                    color: '#ffffff',
                    '& .MuiSvgIcon-root': {
                      color: '#ffffff'
                    }
                  }
                }}
              >
                Sort
              </Button>
              <Menu
                anchorEl={sortAnchorEl}
                open={Boolean(sortAnchorEl)}
                onClose={handleSortClose}
                PaperProps={{
                  sx: {
                    mt: 1,
                    minWidth: 200,
                    borderRadius: '8px',
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
                  }
                }}
              >
                <MenuList>
                  <MenuItem 
                    onClick={() => handleSortSelect('latest')}
                    selected={sortBy === 'latest'}
                    sx={{
                      fontFamily: "'Montserrat', sans-serif",
                      fontSize: '14px',
                      '&.Mui-selected': {
                        backgroundColor: 'rgba(176, 139, 79, 0.1)',
                        '&:hover': {
                          backgroundColor: 'rgba(176, 139, 79, 0.15)'
                        }
                      }
                    }}
                  >
                    Latest Listings
                  </MenuItem>
                  <MenuItem 
                    onClick={() => handleSortSelect('price-high')}
                    selected={sortBy === 'price-high'}
                    sx={{
                      fontFamily: "'Montserrat', sans-serif",
                      fontSize: '14px',
                      '&.Mui-selected': {
                        backgroundColor: 'rgba(176, 139, 79, 0.1)',
                        '&:hover': {
                          backgroundColor: 'rgba(176, 139, 79, 0.15)'
                        }
                      }
                    }}
                  >
                    Price (High to Low)
                  </MenuItem>
                  <MenuItem 
                    onClick={() => handleSortSelect('price-low')}
                    selected={sortBy === 'price-low'}
                    sx={{
                      fontFamily: "'Montserrat', sans-serif",
                      fontSize: '14px',
                      '&.Mui-selected': {
                        backgroundColor: 'rgba(176, 139, 79, 0.1)',
                        '&:hover': {
                          backgroundColor: 'rgba(176, 139, 79, 0.15)'
                        }
                      }
                    }}
                  >
                    Price (Low to High)
                  </MenuItem>
                  <MenuItem 
                    onClick={() => handleSortSelect('beds')}
                    selected={sortBy === 'beds'}
                    sx={{
                      fontFamily: "'Montserrat', sans-serif",
                      fontSize: '14px',
                      '&.Mui-selected': {
                        backgroundColor: 'rgba(176, 139, 79, 0.1)',
                        '&:hover': {
                          backgroundColor: 'rgba(176, 139, 79, 0.15)'
                        }
                      }
                    }}
                  >
                    Beds
                  </MenuItem>
                  <MenuItem 
                    onClick={() => handleSortSelect('baths')}
                    selected={sortBy === 'baths'}
                    sx={{
                      fontFamily: "'Montserrat', sans-serif",
                      fontSize: '14px',
                      '&.Mui-selected': {
                        backgroundColor: 'rgba(176, 139, 79, 0.1)',
                        '&:hover': {
                          backgroundColor: 'rgba(176, 139, 79, 0.15)'
                        }
                      }
                    }}
                  >
                    Baths
                  </MenuItem>
                  <MenuItem 
                    onClick={() => handleSortSelect('year-built')}
                    selected={sortBy === 'year-built'}
                    sx={{
                      fontFamily: "'Montserrat', sans-serif",
                      fontSize: '14px',
                      '&.Mui-selected': {
                        backgroundColor: 'rgba(176, 139, 79, 0.1)',
                        '&:hover': {
                          backgroundColor: 'rgba(176, 139, 79, 0.15)'
                        }
                      }
                    }}
                  >
                    Year Built
                  </MenuItem>
                </MenuList>
              </Menu>
            </Box>
            <Box sx={{ flex: 1, overflow: { xs: 'visible', md: 'auto' }, p: 1 }} data-properties-container>
              {isSearching ? (
                // Mostrar skeletons mientras carga
                <Box 
                  sx={{ 
                    display: 'grid',
                    gridTemplateColumns: 'repeat(2, 1fr)',
                    gap: 2,
                    '& > *': {
                      maxWidth: '100%'
                    }
                  }}
                >
                  {[...Array(6)].map((_, index) => (
                    <Paper
                      key={`skeleton-${index}`}
                      elevation={0}
                      sx={{
                        borderRadius: 2,
                        overflow: 'hidden',
                        border: '1px solid #e0e0e0'
                      }}
                    >
                      <Skeleton variant="rectangular" width="100%" height={200} />
                      <Box sx={{ p: 2 }}>
                        <Skeleton variant="text" width="80%" height={24} sx={{ mb: 1 }} />
                        <Skeleton variant="text" width="60%" height={20} sx={{ mb: 2 }} />
                        <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                          <Skeleton variant="text" width={60} height={20} />
                          <Skeleton variant="text" width={60} height={20} />
                          <Skeleton variant="text" width={60} height={20} />
                        </Box>
                        <Skeleton variant="text" width="40%" height={20} />
                      </Box>
                    </Paper>
                  ))}
                </Box>
              ) : filteredProperties.length === 0 ? (
                <Box sx={{ p: 3, textAlign: 'center' }}>
                  <Typography variant="body1" color="text.secondary">
                    No properties found with the applied filters
                  </Typography>
                </Box>
              ) : (
                <>
                <Box 
                  sx={{ 
                    display: 'grid',
                    gridTemplateColumns: 'repeat(2, 1fr)',
                    gap: 2,
                    alignItems: 'stretch',
                    '& > *': {
                      display: 'flex',
                      flexDirection: 'column'
                    }
                  }}
                >
                    {filteredProperties
                      .slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage)
                      .map((property) => (
                    <Box
                      key={property.id}
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        border: selectedPropertyId === property.id ? '3px solid #1976d2' : '3px solid transparent',
                        borderRadius: 2,
                        transition: 'border-color 0.3s ease',
                        cursor: 'pointer',
                        '& .MuiCard-root': {
                          maxWidth: '100%',
                          width: '100%',
                          height: '100%',
                          display: 'flex',
                          flexDirection: 'column'
                        }
                      }}
                      onClick={() => handlePropertyCardClick(convertToPropertyData(property))}
                    >
                      <PropertyCard
                        data={convertToPropertyData(property)}
                        onViewDetails={handleViewDetails}
                        onFavorite={(propertyData) => {
                          console.log('Agregar a favoritos:', propertyData.id)
                        }}
                      />
                    </Box>
                  ))}
                </Box>
              
              {/* Paginación */}
                  {filteredProperties.length > itemsPerPage && (
                    <Box sx={{ p: 3, borderTop: '1px solid #e0e0e0', mt: 2 }}>
                      <Pagination
                        currentPage={currentPage}
                        totalPages={Math.ceil(filteredProperties.length / itemsPerPage)}
                        totalItems={filteredProperties.length}
                        itemsPerPage={itemsPerPage}
                        onPageChange={(page) => {
                          setCurrentPage(page)
                          // Scroll to top of properties list
                          const propertiesContainer = document.querySelector('[data-properties-container]')
                          if (propertiesContainer) {
                            propertiesContainer.scrollIntoView({ behavior: 'smooth', block: 'start' })
                          }
                        }}
                        showResultsSummary={true}
                        resultsLabel="Properties"
                      />
                </Box>
                  )}
                </>
              )}
            </Box>
          </Paper>
        </Box>

        {/* Columna derecha: Mapa */}
        <Box sx={{ 
          width: { xs: '0%', md: '60%' }, 
          display: { xs: 'none', md: 'block' },
          flexShrink: 0,
          position: 'sticky',
          top: 0,
          alignSelf: 'flex-start',
          height: 'calc(100vh - 100px)',
          minHeight: '600px'
        }}>
          <Paper 
            elevation={0} 
            sx={{ 
              height: '100%', 
              overflow: 'hidden', 
              borderRadius: 3,
              position: 'relative',
              backgroundColor: 'transparent'
            }}
            id="map-container"
          >
            {loadError ? (
              <Alert severity="error" sx={{ m: 2 }}>
                Error loading Google Maps. Please verify your API key.
              </Alert>
            ) : !isLoaded ? (
              <Skeleton variant="rectangular" width="100%" height="100%" animation="wave" />
            ) : (
              <>
                <GoogleMap
                  mapContainerStyle={{ width: '100%', height: '100%' }}
                  center={mapCenter}
                  zoom={zoom}
                  onLoad={setMap}
                  options={{
                    disableDefaultUI: true, // Desactivar todos los controles predeterminados
                    zoomControl: false,
                    streetViewControl: false,
                    mapTypeControl: false,
                    fullscreenControl: false
                  }}
                >
                {selectedProperty && (
                  <InfoWindow
                    position={{ lat: selectedProperty.latitude, lng: selectedProperty.longitude }}
                    onCloseClick={() => {
                      setSelectedProperty(null)
                      setSelectedPropertyId(null)
                    }}
                    options={{
                      maxWidth: 320,
                      pixelOffset: new google.maps.Size(0, -10)
                    }}
                  >
                    <PropertyCard
                      data={convertToPropertyData(selectedProperty)}
                      onViewDetails={handleViewDetails}
                      onFavorite={(propertyData) => {
                        console.log('Agregar a favoritos:', propertyData.id)
                      }}
                      showViewDetails={true} // Ocultar el botón cuando se muestra desde el mapa
                    />
                  </InfoWindow>
                )}
                </GoogleMap>

                {/* Controles personalizados del mapa */}
                <Box
                  sx={{
                    position: 'absolute',
                    right: 16,
                    top: 16,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1,
                    zIndex: 1000
                  }}
                >
                  {/* Botón de pantalla completa (circular) */}
                  <IconButton
                    onClick={handleFullscreen}
                    sx={{
                      width: 48,
                      height: 48,
                      backgroundColor: 'white',
                      borderRadius: '50%',
                      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
                      '&:hover': {
                        backgroundColor: '#f5f5f5',
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)'
                      },
                      transition: 'all 0.2s ease'
                    }}
                  >
                    {isFullscreen ? (
                      <FullscreenExitIcon sx={{ color: '#000', fontSize: 20 }} />
                    ) : (
                      <FullscreenIcon sx={{ color: '#000', fontSize: 20 }} />
                    )}
                  </IconButton>

                  {/* Controles de zoom (píldora vertical) */}
                  <Box
                    sx={{
                      backgroundColor: 'white',
                      borderRadius: '24px',
                      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
                      overflow: 'hidden',
                      display: 'flex',
                      flexDirection: 'column'
                    }}
                  >
                    {/* Botón Zoom In */}
                    <IconButton
                      onClick={handleZoomIn}
                      sx={{
                        width: 48,
                        height: 40,
                        borderRadius: 0,
                        borderBottom: '1px solid rgba(0, 0, 0, 0.1)',
                        '&:hover': {
                          backgroundColor: '#f5f5f5'
                        },
                        transition: 'all 0.2s ease'
                      }}
                    >
                      <AddIcon sx={{ color: '#000', fontSize: 20 }} />
                    </IconButton>

                    {/* Botón Zoom Out */}
                    <IconButton
                      onClick={handleZoomOut}
                      sx={{
                        width: 48,
                        height: 40,
                        borderRadius: 0,
                        '&:hover': {
                          backgroundColor: '#f5f5f5'
                        },
                        transition: 'all 0.2s ease'
                      }}
                    >
                      <RemoveIcon sx={{ color: '#000', fontSize: 20 }} />
                    </IconButton>
                  </Box>
                </Box>
              </>
            )}
          </Paper>
        </Box>
      </Box>

      {/* Filtros */}
      {renderFilters()}
    </Box>
  )
}

export default PropertyMapWidget
